import { API_CONFIG } from '@Web/config/api.config';
import { RestClient as RestClientImpl } from '@Web/clients/RestClient';
import { AuthService } from '@Web/services/AuthService';
import {
  setRefreshToken as persistRefreshToken,
  setRefreshTokenExpiry,
} from '@core/utils/authToken';

// Updated Interface matching the new API Response
export interface RefreshTokenAPIResponse {
  token_type: string;
  refresh_expires_in: number; // in seconds
  access_token: string;
  refresh_token: string;
  expires_in: number; // in seconds
}

// Normalized response for the app to consume (maintaining backward compatibility)
export interface RefreshTokenResult {
  access_token: string;
  refresh_token?: string;
  expiresAt?: string; // ISO string calculated from refresh_expires_in
}

export class RefreshTokenService {
  private static instance: RefreshTokenService;
  private restClient: RestClientImpl;

  private constructor() {
    this.restClient = new RestClientImpl(API_CONFIG.baseUrl);
  }

  public static getInstance(): RefreshTokenService {
    if (!RefreshTokenService.instance) {
      RefreshTokenService.instance = new RefreshTokenService();
    }
    return RefreshTokenService.instance;
  }

  /**
   * Calls refresh endpoint with the new payload structure
   */
  public async refreshToken(): Promise<RefreshTokenResult | null> {
    try {
      const url = API_CONFIG.endpoints.refreshToken;

      // Get the existing refresh token from AuthService
      const currentRefreshToken = AuthService.getInstance().getRefreshToken();

      if (!currentRefreshToken) {
        console.warn(
          '[RefreshTokenService] No refresh token available to send.'
        );
        return null;
      }

      // New Payload Structure
      const payload = {
        refreshToken: currentRefreshToken,
      };

      // Note: Usually when sending the token in the body, we don't need the Bearer header,
      // but we ensure Content-Type is json.
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentRefreshToken}`,
      };

      const response = await this.restClient.post<RefreshTokenAPIResponse>(
        url,
        payload,
        { headers }
      );

      // Validate response
      if (!response || !response.access_token) {
        throw new Error('Invalid refresh response');
      }

      // --- 1. Handle Access Token ---
      AuthService.getInstance().setAccessToken(response.access_token);

      // --- 2. Handle Refresh Token & Expiry (Rotation) ---
      let refreshExpiresAtIso: string | undefined;

      // The new API provides 'refresh_expires_in' in seconds.
      // We must convert this to an absolute ISO string for the app to schedule the next refresh.
      if (response.refresh_expires_in) {
        const expiresInMs = response.refresh_expires_in * 1000;
        refreshExpiresAtIso = new Date(Date.now() + expiresInMs).toISOString();
      }

      if (response.refresh_token) {
        // Update AuthService
        AuthService.getInstance().setRefreshToken(response.refresh_token);

        // Persist to storage
        persistRefreshToken(response.refresh_token);

        if (refreshExpiresAtIso) {
          setRefreshTokenExpiry(refreshExpiresAtIso);
          AuthService.getInstance().setRefreshTokenExpiry(refreshExpiresAtIso);
        }
      }

      // --- 3. Return normalized result for auth.ts ---
      // We return the ISO string in 'expiresAt' so auth.ts logic regarding scheduling remains unchanged.
      return {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        expiresAt: refreshExpiresAtIso,
      };
    } catch (error) {
      console.error('[RefreshTokenService] Failed to refresh token:', error);
      return null;
    }
  }
}
